import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';

import { AuthError } from '@auspost/auth0-uiim-types';

import { errorContent } from '../../../App/common/Notification/Notification.config';
import { apiEndpoint, APP_REQUEST_CONFIGS, appChannel, extLnkMaintenance } from '../../../config/track-config';
import { Notification, NotificationStatus, NotificationType } from '../../../models/Notification';
import { MultiShipmentPayload, Shipment } from '../../../models/shipments';
import { AppAsyncThunkOpts } from '../../../models/Store';
import axiosInstance from '../../../utils/axios.utils';
import { postMxMessage } from '../../../utils/message-exchange.utils';
import { transformShipmentListResponse } from '../../../utils/shipment.utils';
import { AppLocation } from '../../../utils/window.utils';
import { deleteSession } from '../../customer';
import { createNotification } from '../../notification';
import { AppDispatch } from '../../store';
import { GetShipmentErrorData, GetShipmentRequestError } from './getShipment.thunk';

export const getShipmentList = createAsyncThunk<
  Shipment[],
  { ignoreErrors?: boolean } | void,
  AppAsyncThunkOpts<GetShipmentRequestError>
>('shipment/getShipmentList', async (arg = {}, thunkAPI) => {
  const { ignoreErrors = false } = arg || {};
  try {
    const response = await apiGetShipmentList();
    const anonAuthType = response.headers['x-ap-auth-type'] === 'ANON';
    const isAuth = thunkAPI.getState().customer.data?.isAuthenticated;
    const sessionExpired = anonAuthType && isAuth;
    if (sessionExpired && !ignoreErrors) await handleShipmentListError(thunkAPI.dispatch, 401);

    const shipmentListPayload: MultiShipmentPayload = response.data;
    const transformedShipmentList = transformShipmentListResponse(shipmentListPayload);
    return transformedShipmentList;
  } catch (error) {
    const authError = error as AuthError;
    if (
      (authError.isReauthRequiredError && authError.isReauthRequiredError()) ||
      (authError.isLoginRequiredError && authError.isLoginRequiredError())
    ) {
      const status = 401;
      if (!ignoreErrors) await handleShipmentListError(thunkAPI.dispatch, status);
      return thunkAPI.rejectWithValue({
        status,
        data: {
          errorDescription: authError.message,
        },
        isAxiosError: false,
      });
    }

    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<GetShipmentErrorData>;
      const { status, data } = axiosError.response ?? {};
      if (!ignoreErrors) await handleShipmentListError(thunkAPI.dispatch, status, data);
      return thunkAPI.rejectWithValue({
        status,
        data,
        isAxiosError: axiosError.isAxiosError,
      });
    }

    throw error;
  }
});

/**
 * Makes an API call to get watchlist / shipment list from Shipments Gateway API
 */
const apiGetShipmentList = () => {
  const requestConfig = APP_REQUEST_CONFIGS.shipment;
  requestConfig.headers.AP_CHANNEL_NAME = appChannel.watchList;
  return axiosInstance.get(apiEndpoint.shipmentsV1, requestConfig);
};

const handleShipmentListError = async (dispatch: AppDispatch, errorStatus?: number, error?: GetShipmentErrorData) => {
  const createNotificationMsg = (
    dispatch: AppDispatch,
    message = errorContent.generic,
    status: NotificationStatus = NotificationStatus.ERROR
  ) => {
    const notification: Notification = {
      type: NotificationType.ALERT,
      status,
      message,
    };
    dispatch(createNotification(notification));
  };
  switch (errorStatus) {
    case 503:
      const errorDesc = error?.errorDescription;
      if (errorDesc) {
        const mxPayload = {
          heading: 'Tracking is down for maintenance',
          body: errorDesc,
        };
        postMxMessage('CONTENT_OVERRIDE', mxPayload);
      }
      const replaceHist = encodeURIComponent(AppLocation.href.replace(location.origin, ''));
      AppLocation.assign(extLnkMaintenance + '?replaceHist=' + replaceHist);
      break;
    case 401:
      createNotificationMsg(dispatch, errorContent.expiredSession, NotificationStatus.INFO);
      await dispatch(deleteSession());
      break;
    case 500:
      createNotificationMsg(dispatch, errorContent.serverError);
      break;
    default:
      createNotificationMsg(dispatch);
      break;
  }
};
